import { useEffect, useRef, useState } from "react";
import type { IScannerControls } from "@zxing/browser";
import { Camera, Keyboard, X } from "lucide-react";

type Props = {
  onDetected: (barcode: string) => void;
  onClose: () => void;
};

export function BarcodeScanner({ onDetected, onClose }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const controlsRef = useRef<IScannerControls | null>(null);
  const [cameraError, setCameraError] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [manualBarcode, setManualBarcode] = useState("");

  useEffect(() => {
    if (manualMode) return;
    let cancelled = false;
    async function startScanner() {
      const { BrowserMultiFormatReader } = await import("@zxing/browser");
      if (cancelled) return;
      const reader = new BrowserMultiFormatReader();
      await reader.decodeFromVideoDevice(undefined, videoRef.current!, (result, error, controls) => {
        controlsRef.current = controls;
        if (result && !cancelled) {
          navigator.vibrate?.(80);
          controls.stop();
          onDetected(result.getText());
        }
        if (error && error.name !== "NotFoundException") setCameraError("Camera scanning paused. Try manual entry instead.");
      });
    }
    startScanner().catch(() => setCameraError("Camera access is unavailable. Check permission or enter the barcode manually."));
    return () => {
      cancelled = true;
      controlsRef.current?.stop();
    };
  }, [manualMode, onDetected]);

  function submitManual(event: React.FormEvent) {
    event.preventDefault();
    const clean = manualBarcode.replace(/\D/g, "");
    if (clean.length >= 6) onDetected(clean);
  }

  return (
    <div className="scanner-overlay" role="dialog" aria-modal="true" aria-label="Barcode scanner">
      <div className="scanner-toolbar">
        <div>
          <span className="eyebrow light">ADD AN ITEM</span>
          <h2>{manualMode ? "Enter barcode" : "Point at the barcode"}</h2>
        </div>
        <button className="icon-button inverse" onClick={onClose} aria-label="Close scanner"><X /></button>
      </div>

      {manualMode ? (
        <form className="manual-barcode" onSubmit={submitManual}>
          <Keyboard size={36} />
          <label htmlFor="manual-barcode">Barcode number</label>
          <input
            id="manual-barcode"
            inputMode="numeric"
            autoFocus
            placeholder="e.g. 9300657030534"
            value={manualBarcode}
            onChange={(event) => setManualBarcode(event.target.value)}
          />
          <button className="primary-button full" type="submit" disabled={manualBarcode.replace(/\D/g, "").length < 6}>Use this barcode</button>
        </form>
      ) : (
        <div className="camera-stage">
          <video ref={videoRef} muted playsInline aria-label="Live camera preview" />
          <div className="scan-frame" aria-hidden="true"><span /><span /><span /><span /></div>
          <p>{cameraError || "Hold steady — we’ll scan automatically"}</p>
        </div>
      )}

      <button className="scanner-switch" onClick={() => setManualMode((value) => !value)}>
        {manualMode ? <Camera /> : <Keyboard />}
        {manualMode ? "Use camera" : "Enter barcode manually"}
      </button>
    </div>
  );
}
